// =============================================================================
// Webinar Session List Page — Admin
// =============================================================================
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { webinarSessionAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Calendar, Users, ExternalLink, Trash2, Edit, Eye, CheckCircle, Clock, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AppLayout } from '@/components/layout/AppLayout';

const FRONTEND_URL = window.location.origin;

export default function WebinarSessionListPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const { data } = await webinarSessionAPI.list();
      setSessions(data);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette session ?')) return;
    try {
      await webinarSessionAPI.delete(id);
      toast.success('Session supprimée');
      loadSessions();
    } catch {
      toast.error('Erreur');
    }
  };

  const copyLink = (slug) => {
    const url = `${FRONTEND_URL}/webinar-session/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Lien copié !');
  };

  const statusConfig = {
    draft: { label: 'Brouillon', color: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
    published: { label: 'Publié', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    closed: { label: 'Fermé', color: 'bg-red-50 text-red-600 border-red-200' },
  };

  return (
    <AppLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-violet-600 flex items-center justify-center shadow-sm">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              Sessions Webinaire
            </h1>
            <p className="text-muted-foreground mt-1">Gérez les sessions publiques avec inscription et validation</p>
          </div>
          <Button
            onClick={() => navigate('/admin/webinar-sessions/new')}
            className="bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-600 hover:to-violet-700 text-white gap-2"
          >
            <Plus className="w-4 h-4" /> Nouvelle session
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-zinc-200 shadow-sm">
            <Calendar className="w-16 h-16 text-zinc-400 mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Aucune session</h3>
            <p className="text-zinc-500 mb-6">Créez votre première session webinaire publique</p>
            <Button
              onClick={() => navigate('/admin/webinar-sessions/new')}
              className="bg-gradient-to-r from-cyan-500 to-violet-600"
            >
              <Plus className="w-4 h-4 mr-2" /> Créer une session
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {sessions.map(session => {
              const sc = statusConfig[session.status] || statusConfig.draft;
              return (
                <div key={session.id} className="bg-white rounded-xl border border-zinc-200 p-5 hover:border-zinc-300 hover:shadow-md transition-all group">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-zinc-900">{session.title}</h3>
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${sc.color}`}>
                          {sc.label}
                        </span>
                      </div>
                      {session.description && (
                        <p className="text-muted-foreground text-sm mb-3 line-clamp-2">{session.description}</p>
                      )}
                      <div className="flex items-center gap-5 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5" />
                          {session.schedule?.start_at ? new Date(session.schedule.start_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Non planifié'}
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" />
                          <span className="text-cyan-600 font-medium">{session.approved_count || 0}</span> / {session.registration_count || 0} inscriptions
                        </span>
                        <span className="flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5" />
                          Max: {session.max_participants}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {session.status === 'published' && (
                        <Button
                          variant="ghost" size="icon"
                          onClick={() => copyLink(session.slug)}
                          className="h-8 w-8 text-muted-foreground hover:text-cyan-600 hover:bg-cyan-50"
                          title="Copier le lien public"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => navigate(`/admin/webinar-sessions/${session.id}/registrations`)}
                        className="h-8 w-8 text-muted-foreground hover:text-emerald-600 hover:bg-emerald-50"
                        title="Inscriptions"
                      >
                        <Users className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => navigate(`/admin/webinar-sessions/${session.id}/edit`)}
                        className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-muted"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleDelete(session.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
